// First-visit map view: a flying region near the user, guessed from the browser's time zone so we
// don't have to ask for location permission before they have seen the app.

import type { LngLat } from './geo';

type View = { center: LngLat; zoom: number };

const ZONES: Record<string, View> = {
  'America/Los_Angeles': { center: [-120.5, 38.5], zoom: 6 },
  'America/Vancouver': { center: [-121.5, 50], zoom: 6 },
  'America/Denver': { center: [-106.5, 39.5], zoom: 6 },
  'America/Boise': { center: [-114.5, 44], zoom: 6 },
  'America/Edmonton': { center: [-115.5, 51], zoom: 6 },
  'America/Phoenix': { center: [-111.5, 34.2], zoom: 6 },
  'America/Chicago': { center: [-97, 38], zoom: 4.5 },
  'America/New_York': { center: [-78, 39.5], zoom: 5.5 },
  'America/Toronto': { center: [-79.5, 44], zoom: 5.5 },
  'America/Mexico_City': { center: [-99.5, 19.5], zoom: 6 },
  'America/Bogota': { center: [-75, 5], zoom: 6 },
  'America/Lima': { center: [-77, -12], zoom: 6 },
  'America/Santiago': { center: [-70.5, -33.5], zoom: 6 },
  'America/Argentina/Buenos_Aires': { center: [-65.5, -31], zoom: 5 },
  'America/Sao_Paulo': { center: [-44, -21], zoom: 6 },
  'Europe/London': { center: [-3, 54.5], zoom: 5.5 },
  'Europe/Dublin': { center: [-8, 53.3], zoom: 6 },
  'Europe/Madrid': { center: [-3.7, 40.4], zoom: 5.5 },
  'Europe/Lisbon': { center: [-8, 40], zoom: 6 },
  'Atlantic/Canary': { center: [-16, 28.3], zoom: 7 },
  'Europe/Oslo': { center: [9, 61], zoom: 5.5 },
  'Europe/Stockholm': { center: [15, 62], zoom: 5 },
  'Europe/Athens': { center: [22, 39], zoom: 6 },
  'Europe/Istanbul': { center: [29.1, 36.6], zoom: 6 },
  'Europe/Sofia': { center: [25, 42.5], zoom: 6 },
  'Europe/Bucharest': { center: [25, 45.5], zoom: 6 },
  'Europe/Warsaw': { center: [19.5, 49.5], zoom: 6 },
  'Asia/Kathmandu': { center: [84, 28.2], zoom: 7 },
  'Asia/Kolkata': { center: [77, 31.5], zoom: 6 },
  'Asia/Tokyo': { center: [138.5, 36], zoom: 6 },
  'Asia/Seoul': { center: [128, 36.5], zoom: 6.5 },
  'Asia/Shanghai': { center: [105, 32], zoom: 4.5 },
  'Asia/Almaty': { center: [77, 43.2], zoom: 7 },
  'Asia/Tbilisi': { center: [44, 42.2], zoom: 6.5 },
  'Australia/Sydney': { center: [148.5, -35.5], zoom: 6 },
  'Australia/Melbourne': { center: [146.5, -37], zoom: 6 },
  'Australia/Brisbane': { center: [152.5, -27.5], zoom: 6 },
  'Australia/Perth': { center: [116, -32], zoom: 6 },
  'Pacific/Auckland': { center: [171, -43.5], zoom: 5.5 },
  'Africa/Johannesburg': { center: [28, -26], zoom: 5.5 },
  'Africa/Nairobi': { center: [37, -0.5], zoom: 6 },
};

const ALPS: View = { center: [8.5, 46.3], zoom: 6.5 };
const PREFIX: [string, View][] = [
  ['Europe/', ALPS],
  ['America/', { center: [-100, 40], zoom: 3.5 }],
  ['Australia/', { center: [146, -33], zoom: 4.5 }],
  ['Asia/', { center: [95, 30], zoom: 3.5 }],
  ['Africa/', { center: [20, 0], zoom: 3 }],
  ['Pacific/', { center: [172, -40], zoom: 4.5 }],
];

export function startViewForTimeZone(tz: string | undefined): View {
  if (tz && ZONES[tz]) return ZONES[tz];
  for (const [p, v] of PREFIX) if (tz?.startsWith(p)) return v;
  return ALPS;
}

export function guessStartView(): View {
  let tz: string | undefined;
  try {
    tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    /* ignore */
  }
  return startViewForTimeZone(tz);
}
